import {
  ResponseResult,
  ResponseStatus,
  type SubResponse,
} from '@amzn/gbsa-helios-typescript-client';
import { isDataCollectionType } from '../../../shared/domain/responseTypeClassification';

/**
 * A child is answered by:
 *   - its non-empty `responseValue` when it is a DATA-COLLECTION sub
 *     (entry carries `responseType` that is not PASS_FAIL/undefined), OR
 *   - its committed Pass/Fail `result` for a SCORED sub.
 *
 * The two checks are mutually exclusive — a data-collection entry may
 * carry a stray `result` from legacy data, but the wire/cache agree:
 * only the captured value qualifies it as answered. This mirrors the
 * read-side rule in `isSubEntryAnswered` (deriveParentResult.ts) so
 * the wire path and the read path cannot diverge.
 *
 * Pure — no react, no side effects.
 */
export function isSubResponseAnswered(entry: SubResponse): boolean {
  if (isDataCollectionType(entry.responseType)) {
    return entry.responseValue !== undefined && entry.responseValue !== '';
  }
  return entry.result !== undefined;
}

/**
 * Derives the parent's top-level `responseStatus` from the merged
 * children about to go on the wire. THE single status deriver for
 * every sub-question mutation builder (Pass/Fail answer saves and
 * data-collection value saves) so the two paths cannot drift:
 *
 *   - Fewer cached children than defined, or any unanswered child
 *     (see {@link isSubResponseAnswered}) → PENDING
 *   - At least one SCORED child (has a `result`) and every scored
 *     child is NA → NA. Data-collection children never influence
 *     the NA branch — a captured value is data, not applicability.
 *   - Every other complete combination → COMPLETED. A data-only
 *     question (zero scored children) therefore completes without
 *     ever returning NA from this function (the caller overrides to
 *     NA when the self-scoring parent result is NA — see
 *     {@link buildSubQuestionValueUpdate}).
 *
 * Pure — no react, no side effects.
 */
export function deriveSubResponsesStatus(
  merged: readonly SubResponse[],
  totalSubQuestions: number,
): ResponseStatus {
  if (totalSubQuestions <= 0 || merged.length < totalSubQuestions) {
    return ResponseStatus.PENDING;
  }
  if (merged.some((entry) => !isSubResponseAnswered(entry))) {
    return ResponseStatus.PENDING;
  }
  const scored = merged.filter((entry) => entry.result !== undefined);
  const allScoredNa =
    scored.length > 0 && scored.every((entry) => entry.result === ResponseResult.NA);
  return allScoredNa ? ResponseStatus.NA : ResponseStatus.COMPLETED;
}
